import React from 'react';
import { Box, Button, Chip, IconButton, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment-timezone';
import numeral from 'numeral';
import { CalendarTodayIcon, CallIcon, DockLeftIcon, SyncIcon, TransactionIcon, VehicleIcon } from 'components/mui';
const BLUE = '#1D72BC';
const BLUE_BRIGHT = '#2793EE';
const BLUE_FADED = '#DDECFA';
const BLUE_TINT = '#F3F8FD';
const GREEN = '#246F4D';
const TEXT = '#1B1D21';
const MUTED = '#5B626F';
const GREY_FADED = '#F1F3F6';
const BORDER = '#E7EBEF';

/** (801) 404-2663 - the country code is noise on a US caller ID. */
export const formatCallerPhone = phone => {
  const digits = `${phone || ''}`.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length !== 10) {
    return phone || 'Unknown number';
  }
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
};
const formatMoney = total => total === null || total === undefined ? null : numeral(total).format('$0,0.00');
const SectionLabel = ({
  children,
  color = MUTED
}) => <Typography sx={{
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: 1.5,
  mb: '3px',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color
}}>
    {children}
  </Typography>;
const Row = ({
  icon,
  label,
  highlight,
  children
}) => <Stack direction="row" spacing={2} sx={{
  p: highlight ? 1.5 : 0,
  mx: highlight ? -1.5 : 0,
  borderRadius: '10px',
  backgroundColor: highlight ? BLUE_TINT : 'transparent'
}}>
    <Box sx={{
    'width': 34,
    'height': 34,
    'flexShrink': 0,
    'borderRadius': '8px',
    'display': 'flex',
    'alignItems': 'center',
    'justifyContent': 'center',
    'backgroundColor': highlight ? BLUE_FADED : GREY_FADED,
    '& svg': {
      width: 20,
      height: 20
    }
  }}>
      {icon}
    </Box>
    <Box sx={{
    flexGrow: 1,
    minWidth: 0
  }}>
      <SectionLabel color={highlight ? BLUE : MUTED}>{label}</SectionLabel>
      {children}
    </Box>
  </Stack>;
const Primary = ({
  children
}) => <Typography sx={{
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: 1.5,
  color: TEXT
}}>{children}</Typography>;
const Secondary = ({
  children
}) => <Typography noWrap sx={{
  fontSize: '14px',
  lineHeight: 1.5,
  color: MUTED
}}>
    {children}
  </Typography>;
const Pill = ({
  label,
  color,
  background
}) => <Chip size="small" label={label} sx={{
  'fontSize': '13px',
  'fontWeight': 500,
  'borderRadius': '999px',
  color,
  'backgroundColor': background,
  'height': 26,
  'flexShrink': 0,
  '& .MuiChip-label': {
    px: '10px'
  }
}} />;
const vehicleName = vehicle => [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
const Details = ({
  snapshot
}) => {
  const {
    last_transaction: transaction,
    subscriptions,
    next_appointment: appointment,
    vehicles = []
  } = snapshot;
  const hasSubscriptions = subscriptions?.total > 0;
  if (!transaction && !hasSubscriptions && !appointment && !vehicles.length) {
    return null;
  }
  return <Stack spacing={2} sx={{
    px: 2.5,
    py: 2.25,
    borderTop: `1px solid ${BORDER}`
  }}>
      {transaction && <Row icon={<TransactionIcon fill={MUTED} />} label="Last transaction">
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Primary>{transaction.when ? moment(transaction.when).format('MMM D, YYYY') : 'Undated'}</Primary>
            <Primary>{formatMoney(transaction.total)}</Primary>
          </Stack>
          {transaction.location_name && <Secondary>{transaction.location_name}</Secondary>}
        </Row>}

      {hasSubscriptions && <Row icon={<SyncIcon fill={MUTED} />} label="Subscriptions">
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Primary>
              {subscriptions.active > 0 ? `${subscriptions.active} active plan${subscriptions.active === 1 ? '' : 's'}` : 'No active plans'}
            </Primary>
            {subscriptions.active > 0 ? <Pill label="Active" color={GREEN} background="#E4F5EC" /> : <Pill label="Inactive" color={MUTED} background={GREY_FADED} />}
          </Stack>
        </Row>}

      {vehicles.length > 0 && <Row icon={<VehicleIcon fill={MUTED} />} label={vehicles.length === 1 ? 'Vehicle' : 'Vehicles'}>
          <Primary>{vehicleName(vehicles[0]) || 'Vehicle on file'}</Primary>
          {vehicles.length > 1 && <Secondary>+{vehicles.length - 1} more</Secondary>}
        </Row>}

      {appointment && <Row icon={<CalendarTodayIcon fill={BLUE} />} label="Next appointment" highlight>
          {/* Naive on purpose: the location's wall clock, not the viewer's. */}
          <Primary>{moment(appointment.when_naive).format('ddd, MMM D · h:mm A')}</Primary>
          <Secondary>{[appointment.description, appointment.location_name].filter(Boolean).join(' · ')}</Secondary>
        </Row>}
    </Stack>;
};
const LoadingDetails = () => <Stack spacing={2} sx={{
  px: 2.5,
  py: 2.25,
  borderTop: `1px solid ${BORDER}`
}}>
    {[0, 1, 2].map(i => <Stack key={i} direction="row" spacing={2}>
        <Skeleton variant="rounded" width={34} height={34} />
        <Box sx={{
      flexGrow: 1
    }}>
          <Skeleton width="40%" />
          <Skeleton width="80%" />
        </Box>
      </Stack>)}
  </Stack>;

/**
 * The incoming-call card. Presentational only - CallPopHost decides which calls get one and
 * what the buttons do.
 *
 * `onAnswer` is only passed when this browser can actually answer the call; the ticket asks
 * for the button to be absent rather than disabled otherwise. `closeLabel` is there because
 * on a ringing softphone call the X declines the call rather than just hiding the card.
 */
export const CallPop = ({
  callerPhone,
  callerName,
  snapshot,
  loading,
  onAnswer,
  onDetails,
  onClose,
  closeLabel = 'Dismiss'
}) => {
  const name = snapshot ? [snapshot.first_name, snapshot.last_name].filter(Boolean).join(' ') : callerName;
  const phone = formatCallerPhone(snapshot?.phone || callerPhone);
  return <Paper elevation={8} sx={{
    width: 432,
    borderRadius: '16px',
    overflow: 'hidden'
  }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{
      px: 2.5,
      py: 1.5,
      backgroundColor: BLUE_TINT,
      borderBottom: `1px solid ${BORDER}`
    }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: BLUE_BRIGHT
        }} />
          <Typography sx={{
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          color: BLUE
        }}>
            Incoming call
          </Typography>
        </Stack>
        <Tooltip title={closeLabel}>
          <IconButton size="small" aria-label={closeLabel} onClick={onClose}>
            <CloseIcon fontSize="small" sx={{
            color: MUTED
          }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{
      px: 2.5,
      py: 2.25
    }}>
        <Box sx={{
        width: 52,
        height: 52,
        flexShrink: 0,
        borderRadius: '50%',
        backgroundColor: BLUE_BRIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
          <CallIcon fill="#fff" />
        </Box>
        <Stack spacing={0.5} sx={{
        minWidth: 0
      }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography noWrap sx={{
            fontSize: '26px',
            fontWeight: 700,
            lineHeight: 1.2,
            color: TEXT
          }}>
              {name || phone}
            </Typography>
            {snapshot && (snapshot.is_prospect ? <Pill label="Prospect" color={MUTED} background={GREY_FADED} /> : <Pill label={`Value ${snapshot.value_segment}`} color="#175FA0" background={BLUE_FADED} />)}
          </Stack>
          {name && <Typography sx={{
          fontSize: '15px',
          lineHeight: 1.4,
          color: TEXT
        }}>{phone}</Typography>}
          {snapshot?.email && <Secondary>{snapshot.email}</Secondary>}
        </Stack>
      </Stack>

      {loading && <LoadingDetails />}
      {/* A prospect has no history to show, so their card stops at who is calling. */}
      {snapshot && !snapshot.is_prospect && <Details snapshot={snapshot} />}

      {(onAnswer || onDetails) && <Stack direction="row" spacing={1.5} sx={{
      px: 2.5,
      py: 2.25,
      borderTop: `1px solid ${BORDER}`
    }}>
          {onAnswer && <Button fullWidth variant="contained" onClick={onAnswer} startIcon={<CallIcon fill="#fff" />} sx={{
        // Narrower than Customer details, as in the design.
        'flex': 2,
        'backgroundColor': GREEN,
        '&:hover': {
          backgroundColor: GREEN
        },
        'textTransform': 'none',
        'fontSize': '16px',
        'borderRadius': '8px',
        'py': 1.5
      }}>
              Answer
            </Button>}
          {onDetails && <Button fullWidth variant="outlined" onClick={onDetails} startIcon={<DockLeftIcon fill={BLUE} />} sx={{
        'flex': 3,
        'color': BLUE,
        'borderColor': '#DADADA',
        '&:hover': {
          borderColor: '#DADADA',
          backgroundColor: BLUE_TINT
        },
        'textTransform': 'none',
        'fontSize': '16px',
        'borderRadius': '8px',
        'py': 1.5
      }}>
              Customer details
            </Button>}
        </Stack>}
    </Paper>;
};
export default CallPop;